import { DOMSerializer, type Schema } from "@tiptap/pm/model";

/** Rich clipboard targets recognize a checkbox directly inside its li.
 * Preserve task attributes for internal paste, while exporting simple items as
 * tight list HTML so Markdown editors do not manufacture paragraph spacing. */
export function createClipboardHtmlSerializer(schema: Schema): DOMSerializer {
  const serializer = DOMSerializer.fromSchema(schema);
  const clipboard = new DOMSerializer({
    ...serializer.nodes,
    ...(schema.nodes.taskItem ? {
      taskItem: node => [
        "li", { "data-type": "taskItem", "data-checked": String(!!node.attrs.checked) },
        ["input", { type: "checkbox", ...(node.attrs.checked ? { checked: "checked" } : {}) }],
        ["div", { "data-checklist-clipboard-content": "" }, 0],
      ],
    } : {}),
  }, serializer.marks);
  const serializeFragment = clipboard.serializeFragment.bind(clipboard);
  clipboard.serializeFragment = (fragment, options, target) => {
    const dom = serializeFragment(fragment, options, target);
    // Remove the temporary task-content wrappers, keeping their children in order.
    dom.querySelectorAll?.("div[data-checklist-clipboard-content]").forEach(wrapper => {
      wrapper.replaceWith(...Array.from(wrapper.childNodes));
    });
    // A lone paragraph wrapper makes HTML-to-Markdown importers treat a simple
    // list item as a loose paragraph (or put checkbox text on a separate line).
    // Inline content is wrapped back into a paragraph by the editor's list schema.
    // Keep empty, attributed, and multi-paragraph items intact.
    dom.querySelectorAll?.("li").forEach(item => {
      const blocks = Array.from(item.children).filter(child => child.tagName !== "INPUT");
      const first = blocks[0];
      if (first?.tagName === "P" && first.childNodes.length > 0 && first.attributes.length === 0
        && blocks.slice(1).every(child => child.tagName === "UL" || child.tagName === "OL")) {
        first.replaceWith(...Array.from(first.childNodes));
      }
    });
    return dom;
  };
  return clipboard;
}
